import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, BarChart2, Bell, CreditCard } from "lucide-react";
import { getAlerts, deleteAlert, type AlertDto } from "../lib/alerts-api.js";
import { getBillingStatus } from "../lib/billing-api.js";
import { SubscriptionStatusCard } from "../components/SubscriptionStatusCard.js";
import { AlertCard } from "../components/AlertCard.js";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog.js";
import { AppFooter } from "../components/AppFooter.js";
import { ROUTES } from "../routes.js";

export function AlertsScreen(): React.ReactElement {
  const queryClient = useQueryClient();
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  const { data: alerts = [], isLoading: isLoadingAlerts } = useQuery({
    queryKey: ["alerts"],
    queryFn: getAlerts,
  });

  const { data: billingStatus, isLoading: isLoadingBilling } = useQuery({
    queryKey: ["billingStatus"],
    queryFn: getBillingStatus,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteAlert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
      setDeleteTargetId(null);
    },
  });

  const canEdit = billingStatus?.entitlements?.canEditAlerts ?? true;
  const isLimitReached = alerts.length >= 5;

  return (
    <div data-testid="alerts-screen" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Header — separate block above card, text on the left, right 94px reserved */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "4px 94px 4px 4px",
          minHeight: "56px",
          flexShrink: 0,
          boxSizing: "border-box",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: "22px",
            lineHeight: "26px",
            fontWeight: 700,
            color: "var(--color-graphite)",
            letterSpacing: "-0.02em",
          }}
        >
          CorgiWork
        </h1>
        <p
          style={{
            margin: "2px 0 0 0",
            fontSize: "13px",
            lineHeight: "16px",
            color: "var(--color-slate)",
          }}
        >
          Поиск вакансий на удаленке
        </p>
      </div>

      {/* Subscription Card */}
      <SubscriptionStatusCard status={billingStatus} isLoading={isLoadingBilling} />

      {/* Manage Subscription Button */}
      <Link
        to={ROUTES.SUBSCRIPTION}
        data-testid="manage-subscription-btn"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "100%",
          padding: "14px",
          backgroundColor: "var(--color-paper)",
          color: "var(--color-graphite)",
          borderRadius: "var(--radius-button)",
          fontSize: "14px",
          fontWeight: 600,
          textDecoration: "none",
          boxShadow: "var(--shadow-card)",
          boxSizing: "border-box",
        }}
      >
        <CreditCard size={16} />
        Управление подпиской
      </Link>

      {/* Alerts Section Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 4px",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: 600,
            color: "var(--color-graphite)",
          }}
        >
          Отслеживаемые профили
        </h2>
        <span
          data-testid="alerts-count"
          style={{
            fontSize: "13px",
            fontWeight: 500,
            color: "var(--color-slate)",
          }}
        >
          {alerts.length} из 5
        </span>
      </div>

      {/* Alerts List / Loading / Empty */}
      {isLoadingAlerts ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {[1, 2].map((k) => (
            <div
              key={k}
              style={{
                height: "120px",
                backgroundColor: "var(--color-paper)",
                borderRadius: "var(--radius-card)",
                boxShadow: "var(--shadow-card)",
              }}
            />
          ))}
        </div>
      ) : alerts.length === 0 ? (
        <div
          data-testid="alerts-empty-state"
          style={{
            backgroundColor: "var(--color-paper)",
            borderRadius: "var(--radius-card)",
            boxShadow: "var(--shadow-floating)",
            padding: "36px 20px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "var(--color-gold-wash)",
              color: "var(--color-muted-gold)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Bell size={24} />
          </div>
          <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 600 }}>У вас пока нет профилей</h3>
          <p style={{ margin: 0, fontSize: "14px", color: "var(--color-slate)", maxWidth: "280px" }}>
            Настройте фильтры, чтобы получать только подходящие вакансии прямо в Telegram.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {alerts.map((alert: AlertDto) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              canEdit={canEdit}
              onDelete={(id) => setDeleteTargetId(id)}
            />
          ))}
        </div>
      )}

      {/* Primary CTA: Create Alert */}
      {!canEdit ? (
        <button
          data-testid="create-alert-disabled-btn"
          disabled
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            padding: "14px",
            backgroundColor: "var(--color-canvas)",
            color: "var(--color-slate)",
            border: "1px solid var(--color-frost-border)",
            borderRadius: "var(--radius-button)",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "not-allowed",
            boxSizing: "border-box",
          }}
        >
          <Plus size={16} />
          Добавить профиль
        </button>
      ) : isLimitReached ? (
        <button
          data-testid="create-alert-disabled-btn"
          disabled
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            padding: "14px",
            backgroundColor: "var(--color-canvas)",
            color: "var(--color-slate)",
            border: "1px dashed var(--color-frost-border)",
            borderRadius: "var(--radius-button)",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "not-allowed",
            boxSizing: "border-box",
          }}
        >
          Лимит профилей достигнут (5 из 5)
        </button>
      ) : (
        <Link
          to={ROUTES.ALERT_NEW}
          data-testid="create-alert-btn"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            padding: "14px",
            backgroundColor: "var(--color-muted-gold)",
            color: "var(--color-paper)",
            borderRadius: "var(--radius-button)",
            fontSize: "14px",
            fontWeight: 600,
            textDecoration: "none",
            boxShadow: "var(--shadow-card)",
            boxSizing: "border-box",
          }}
        >
          <Plus size={16} />
          Добавить профиль
        </Link>
      )}

      {/* Bottom Button: Stats */}
      <Link
        to={ROUTES.STATS}
        data-testid="stats-btn"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "100%",
          padding: "14px",
          backgroundColor: "var(--color-paper)",
          color: "var(--color-graphite)",
          borderRadius: "var(--radius-button)",
          fontSize: "14px",
          fontWeight: 600,
          textDecoration: "none",
          boxShadow: "var(--shadow-card)",
          boxSizing: "border-box",
          marginTop: "4px",
        }}
      >
        <BarChart2 size={16} />
        Статистика вакансий
      </Link>

      {/* App Footer */}
      <AppFooter />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmDialog
        isOpen={deleteTargetId !== null}
        alertId={deleteTargetId}
        isDeleting={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTargetId !== null) {
            deleteMutation.mutate(deleteTargetId);
          }
        }}
        onCancel={() => setDeleteTargetId(null)}
      />
    </div>
  );
}

export default AlertsScreen;
